import { type PropsWithChildren, useContext, useRef, useState, useLayoutEffect } from 'react';
import { createStyles } from '@mantine/core';

import { Routes } from '~/domains/common/constants/routes';
import AppBanners from '~/domains/common/components/AppBanners/AppBanners';
import { CHAT_WIDGET_HEIGHT } from '~/domains/design-system/components/Chat/ChatWidget/constants';

import GamecenterHeader from '../GamecenterHeader/GamecenterHeader';
import { useContestContext } from '../../context/ContestContext/ContestContext';
import GamecenterPrevPathContext from '../../context/GamecenterPrevPathContext/GamecenterPrevPathContext';
import { useChatWidgetVisible } from '../../hooks/useChatWidgetVisible';
import GamecenterLayoutWithChat, {
  type ChatLayoutMode,
} from '../GamecenterLayoutWithChat/GamecenterLayoutWithChat';
import { ContestChatPanel } from '../ContestChat/ContestChatPanel';

import GamecenterScheduledNav from './GamecenterScheduleNav';

const useStyles = createStyles((theme, { showChatWidget }: { showChatWidget: boolean }) => ({
  container: {
    minHeight: '100%',
    paddingBottom: showChatWidget ? CHAT_WIDGET_HEIGHT.SAFE_AREA : 0,
  },
  headerContainer: {
    position: 'sticky',
    top: 0,
    zIndex: theme.other.zIndex.header,
  },
}));

function GamecenterScheduledLayout({
  children,
  headerSlot,
  chatLayoutMode,
}: PropsWithChildren<{
  headerSlot?: JSX.Element;
  chatLayoutMode?: ChatLayoutMode;
}>) {
  const showChatWidget = useChatWidgetVisible();
  const { classes } = useStyles({ showChatWidget });
  const { prevLocationBeforeGamecenter } = useContext(GamecenterPrevPathContext);
  const { contest } = useContestContext();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const measureHeader = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    measureHeader();

    const resizeObserver = new ResizeObserver(measureHeader);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const prevRoute = prevLocationBeforeGamecenter ?? Routes.myEntries();

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer} ref={headerRef} data-gamecenter-sticky-header>
        <GamecenterHeader closeHref={prevRoute} contest={contest} />
        <GamecenterScheduledNav contest={contest} />
        <AppBanners />
        {headerSlot}
      </div>
      <GamecenterLayoutWithChat
        chatSlot={<ContestChatPanel />}
        layoutMode={chatLayoutMode}
        headerHeight={headerHeight}
      >
        {children}
      </GamecenterLayoutWithChat>
    </div>
  );
}

export default GamecenterScheduledLayout;
